"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { RefreshCcwIcon } from "lucide-react"
import Link from "next/link"
import { toast } from "sonner"
import type { CartSnapshot } from "@/api/cart"
import {
  getCartItems,
  removeCartItem,
  updateCartItemQuantity,
} from "@/api/cart"
import {
  applyQuantityOptimisticChange,
  applyRemoveOptimisticChange,
  toCartPageItems,
} from "@/components/account/cart/cart-state"
import { CartCouponSection } from "@/components/account/cart/coupon"
import { Button } from "@/components/ui/button"
import { CartShippingSection } from "~/components/account/cart/shipping"
import { CartSummarySection } from "~/components/account/cart/summary"
import { CartItemsTable } from "~/components/account/cart/table"

export default function Page() {
  const queryClient = useQueryClient()

  const cartQuery = useQuery({
    queryKey: ["cart", "items"],
    queryFn: getCartItems,
    retry: false,
  })

  const updateQuantityMutation = useMutation({
    mutationFn: updateCartItemQuantity,
    onMutate: async ({ key, quantity }) => {
      await queryClient.cancelQueries({ queryKey: ["cart", "items"] })

      const previousSnapshot = queryClient.getQueryData<CartSnapshot>([
        "cart",
        "items",
      ])

      if (previousSnapshot) {
        queryClient.setQueryData(
          ["cart", "items"],
          applyQuantityOptimisticChange(previousSnapshot, key, quantity)
        )
      }

      const toastId = toast.loading("Atualizando quantidade...")

      return { previousSnapshot, toastId }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSnapshot) {
        queryClient.setQueryData(["cart", "items"], context.previousSnapshot)
      }

      toast.error("Nao foi possivel atualizar a quantidade.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        toast.error(
          response.message?.trim() ||
            "Nao foi possivel atualizar a quantidade.",
          {
            id: context?.toastId,
          }
        )

        return
      }

      toast.success("Quantidade atualizada.", {
        id: context?.toastId,
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
    },
  })

  const removeItemMutation = useMutation({
    mutationFn: removeCartItem,
    onMutate: async ({ key }) => {
      await queryClient.cancelQueries({ queryKey: ["cart", "items"] })

      const previousSnapshot = queryClient.getQueryData<CartSnapshot>([
        "cart",
        "items",
      ])

      if (previousSnapshot) {
        queryClient.setQueryData(
          ["cart", "items"],
          applyRemoveOptimisticChange(previousSnapshot, key)
        )
      }

      const toastId = toast.loading("Removendo item do carrinho...")

      return { previousSnapshot, toastId }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSnapshot) {
        queryClient.setQueryData(["cart", "items"], context.previousSnapshot)
      }

      toast.error("Nao foi possivel remover o item.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        toast.error(
          response.message?.trim() || "Nao foi possivel remover o item.",
          {
            id: context?.toastId,
          }
        )

        return
      }

      toast.success("Item removido do carrinho.", { id: context?.toastId })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
    },
  })

  const snapshot = cartQuery.data
  const items = snapshot ? toCartPageItems(snapshot) : []
  const subtotal = snapshot?.totals.subtotal ?? 0
  const discountTotal = snapshot?.totals.discountTotal ?? 0
  const total = snapshot?.totals.total ?? 0
  const productsLabel = `${items.length} ${items.length === 1 ? "produto" : "produtos"}`

  const updateQuantity = (id: string, nextQuantity: number) => {
    if (nextQuantity <= 0) {
      return
    }

    updateQuantityMutation.mutate({
      key: id,
      quantity: nextQuantity,
    })
  }

  const removeItem = (id: string) => {
    removeItemMutation.mutate({ key: id })
  }

  const refreshCart = async () => {
    await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
  }

  return (
    <section className='w-full px-4 md:px-0'>
      <div className='rounded-2xl border bg-linear-to-b from-white to-zinc-50 p-4 shadow-xs md:p-6'>
        <div className='mb-6 flex flex-wrap items-end justify-between gap-3'>
          <div className='space-y-1'>
            <p className='font-medium text-muted-foreground text-xs uppercase tracking-[0.14em]'>
              Checkout
            </p>
            <h1 className='font-semibold text-3xl'>Carrinho</h1>
          </div>
          <div className='rounded-full border bg-white px-3 py-1 font-medium text-muted-foreground text-sm'>
            {productsLabel}
          </div>
        </div>

        {cartQuery.isError ? (
          <div className='mb-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4'>
            <p className='font-medium text-destructive'>
              Nao foi possivel carregar o carrinho.
            </p>
            <p className='mt-1 text-muted-foreground text-sm'>
              Confira sua conexao e tente novamente.
            </p>
            <Button
              className='mt-3'
              onClick={async () => {
                await cartQuery.refetch()
              }}
              type='button'
              variant='outline'
            >
              Tentar novamente
            </Button>
          </div>
        ) : null}

        {cartQuery.isLoading && !snapshot ? (
          <div className='rounded-xl border border-dashed p-8 text-center text-muted-foreground'>
            Carregando carrinho...
          </div>
        ) : (
          <CartItemsTable
            items={items}
            onRemoveItem={removeItem}
            onUpdateQuantity={updateQuantity}
          />
        )}

        <div className='mt-6 flex flex-col gap-3 border-border border-b pb-6 md:flex-row md:items-center md:justify-between'>
          <Button asChild className='w-full md:w-auto'>
            <Link href='/shop'>Retornar a Loja</Link>
          </Button>

          <Button
            className='w-full md:w-auto'
            onClick={refreshCart}
            type='button'
            variant='outline'
          >
            <RefreshCcwIcon aria-hidden='true' size={14} />
            Atualizar Carrinho de Compras
          </Button>
        </div>

        <div className='mt-8 grid gap-6 lg:grid-cols-[1fr_1fr_320px]'>
          <CartCouponSection />
          <CartShippingSection />
          <CartSummarySection
            discountTotal={discountTotal}
            subtotal={subtotal}
            total={total}
          />
        </div>
      </div>
    </section>
  )
}
